/**
 * Corrective Action Repository
 * 
 * Data access layer for corrective actions:
 * - CRUD operations
 * - Advanced querying and filtering
 * - Status workflow management
 * - Photo attachment handling
 * - Statistics and reporting
 */

import { BaseRepository } from '../BaseRepository';
import { MARSXDatabase } from '../db';
import type { CorrectiveAction, CorrectiveActionStatus, CorrectiveActionFilters } from '../../../types/database';
import { liveQuery } from 'dexie';
import { isBefore } from 'date-fns';

export class CorrectiveActionRepository extends BaseRepository<CorrectiveAction> {
  protected db: MARSXDatabase;
  
  constructor(db: MARSXDatabase) {
    super(db.corrective_actions, 'corrective_actions');
    this.db = db;
  }

  /**
   * Get actions by organization
   */
  async getByOrganization(organizationId: string): Promise<CorrectiveAction[]> {
    return this.db.corrective_actions
      .where('organizationId')
      .equals(organizationId)
      .toArray();
  }

  /**
   * Get actions by organization (reactive)
   */
  getByOrganization$(organizationId: string) {
    return liveQuery(() =>
      this.db.corrective_actions
        .where('organizationId')
        .equals(organizationId)
        .toArray()
    );
  }

  /**
   * Get actions by ship
   */
  async getByShip(shipId: string): Promise<CorrectiveAction[]> {
    return this.db.corrective_actions
      .where('vesselId')
      .equals(shipId)
      .toArray();
  }

  /**
   * Get actions by status
   */
  async getByStatus(
    organizationId: string,
    status: CorrectiveActionStatus
  ): Promise<CorrectiveAction[]> {
    return this.db.corrective_actions
      .where('[organizationId+status]')
      .equals([organizationId, status])
      .toArray();
  }

  /**
   * Get actions by multiple statuses
   */
  async getByStatuses(
    organizationId: string,
    statuses: CorrectiveActionStatus[]
  ): Promise<CorrectiveAction[]> {
    const allActions = await this.getByOrganization(organizationId);
    return allActions.filter((action) => statuses.includes(action.status));
  }

  /**
   * Get actions by severity
   */
  async getBySeverity(
    organizationId: string,
    severity: CorrectiveAction['severity']
  ): Promise<CorrectiveAction[]> {
    const allActions = await this.getByOrganization(organizationId);
    return allActions.filter((action) => action.severity === severity);
  }

  /**
   * Get overdue actions
   */
  async getOverdueActions(organizationId: string): Promise<CorrectiveAction[]> {
    const allActions = await this.getByOrganization(organizationId);
    const now = new Date();

    return allActions.filter(
      (action) =>
        action.dueDate &&
        isBefore(new Date(action.dueDate), now) &&
        action.status !== 'completed' &&
        action.status !== 'verified' &&
        action.status !== 'cancelled'
    );
  }

  /**
   * Get actions assigned to user
   */
  async getAssignedTo(
    organizationId: string,
    userId: string
  ): Promise<CorrectiveAction[]> {
    return this.db.corrective_actions
      .where('[organizationId+assignedTo]')
      .equals([organizationId, userId])
      .toArray();
  }

  /**
   * Get actions reported by user
   */
  async getReportedBy(
    organizationId: string,
    userId: string
  ): Promise<CorrectiveAction[]> {
    return this.db.corrective_actions
      .where('[organizationId+reportedBy]')
      .equals([organizationId, userId])
      .toArray();
  }

  /**
   * Get actions by date range
   */
  async getByDateRange(
    organizationId: string,
    startDate: Date,
    endDate: Date
  ): Promise<CorrectiveAction[]> {
    const allActions = await this.getByOrganization(organizationId);

    return allActions.filter((action) => {
      const reportedDate = new Date(action.reportedDate);
      return reportedDate >= startDate && reportedDate <= endDate;
    });
  }

  /**
   * Advanced filtering
   */
  async filterActions(filters: CorrectiveActionFilters): Promise<CorrectiveAction[]> {
    let actions: CorrectiveAction[] = [];

    // Start with organization or ship filter
    if (filters.vesselId) {
      actions = await this.getByShip(filters.vesselId);
    } else {
      return []; // Must provide at least ship or org
    }

    // Apply status filter
    if (filters.status && filters.status.length > 0) {
      actions = actions.filter((action) => filters.status!.includes(action.status));
    }

    // Apply severity filter
    if (filters.severity && filters.severity.length > 0) {
      actions = actions.filter((action) => filters.severity!.includes(action.severity));
    }

    // Apply category filter
    if (filters.category && filters.category.length > 0) {
      actions = actions.filter((action) =>
        filters.category!.some((cat) => action.category.includes(cat))
      );
    }

    // Apply assigned to filter
    if (filters.assignedTo && filters.assignedTo.length > 0) {
      actions = actions.filter(
        (action) => action.assignedTo && filters.assignedTo!.includes(action.assignedTo)
      );
    }

    // Apply reported by filter
    if (filters.reportedBy && filters.reportedBy.length > 0) {
      actions = actions.filter((action) =>
        filters.reportedBy!.includes(action.reportedBy)
      );
    }

    // Apply date range filter
    if (filters.dateFrom || filters.dateTo) {
      actions = actions.filter((action) => {
        const reportedDate = new Date(action.reportedDate);
        if (filters.dateFrom && reportedDate < filters.dateFrom) {
          return false;
        }
        if (filters.dateTo && reportedDate > filters.dateTo) {
          return false;
        }
        return true;
      });
    }

    // Apply overdue filter
    if (filters.overdue) {
      const now = new Date();
      actions = actions.filter(
        (action) =>
          action.dueDate &&
          isBefore(new Date(action.dueDate), now) &&
          action.status !== 'completed' &&
          action.status !== 'verified'
      );
    }

    // Apply due soon filter (within 7 days)
    if (filters.dueSoon) {
      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      actions = actions.filter(
        (action) =>
          action.dueDate &&
          new Date(action.dueDate) >= now &&
          new Date(action.dueDate) <= sevenDaysFromNow &&
          action.status !== 'completed' &&
          action.status !== 'verified'
      );
    }

    return actions;
  }

  /**
   * Update action status
   */
  async updateStatus(
    actionId: string,
    status: CorrectiveActionStatus,
    userId: string
  ): Promise<void> {
    const updates: Partial<CorrectiveAction> = {
      status,
      updatedAt: new Date(),
    };

    if (status === 'completed') {
      updates.completedDate = new Date();
    } else if (status === 'verified') {
      updates.verifiedBy = userId;
      updates.verifiedDate = new Date();
    }

    await this.update(actionId, updates);
  }

  /**
   * Assign action to user
   */
  async assignAction(actionId: string, userId: string): Promise<void> {
    await this.update(actionId, {
      assignedTo: userId,
      status: 'in_progress',
      updatedAt: new Date(),
    });
  }

  /**
   * Add deficiency photo
   */
  async addDeficiencyPhoto(actionId: string, photoUrl: string): Promise<void> {
    const action = await this.getById(actionId);
    if (!action) {
      throw new Error('Action not found');
    }

    const photos = action.deficiencyPhotos || [];
    photos.push(photoUrl);

    await this.update(actionId, {
      deficiencyPhotos: photos,
      updatedAt: new Date(),
    });
  }

  /**
   * Add resolution photo
   */
  async addResolutionPhoto(actionId: string, photoUrl: string): Promise<void> {
    const action = await this.getById(actionId);
    if (!action) {
      throw new Error('Action not found');
    }

    const photos = action.resolutionPhotos || [];
    photos.push(photoUrl);

    await this.update(actionId, {
      resolutionPhotos: photos,
      updatedAt: new Date(),
    });
  }

  /**
   * Remove photo
   */
  async removePhoto(
    actionId: string,
    photoUrl: string,
    photoType: 'deficiency' | 'resolution'
  ): Promise<void> {
    const action = await this.getById(actionId);
    if (!action) {
      throw new Error('Action not found');
    }

    const field = photoType === 'deficiency' ? 'deficiencyPhotos' : 'resolutionPhotos';
    const photos = action[field] || [];
    const filtered = photos.filter((url) => url !== photoUrl);

    await this.update(actionId, {
      [field]: filtered,
      updatedAt: new Date(),
    });
  }

  /**
   * Get statistics
   */
  async getStatistics(organizationId: string): Promise<{
    total: number;
    byStatus: Record<CorrectiveActionStatus, number>;
    bySeverity: Record<CorrectiveAction['severity'], number>;
    overdue: number;
    completedThisMonth: number;
    averageResolutionDays: number;
  }> {
    const allActions = await this.getByOrganization(organizationId);
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const stats = {
      total: allActions.length,
      byStatus: {
        open: 0,
        in_progress: 0,
        pending_verification: 0,
        completed: 0,
        verified: 0,
        cancelled: 0,
      } as Record<CorrectiveActionStatus, number>,
      bySeverity: {
        minor: 0,
        moderate: 0,
        major: 0,
        critical: 0,
      } as Record<CorrectiveAction['severity'], number>,
      overdue: 0,
      completedThisMonth: 0,
      averageResolutionDays: 0,
    };

    let totalResolutionDays = 0;
    let completedCount = 0;

    for (const action of allActions) {
      // Count by status
      stats.byStatus[action.status]++;

      // Count by severity
      stats.bySeverity[action.severity]++;

      // Count overdue
      if (
        action.dueDate &&
        isBefore(new Date(action.dueDate), now) &&
        action.status !== 'completed' &&
        action.status !== 'verified'
      ) {
        stats.overdue++;
      }

      // Count completed this month
      if (
        action.completedDate &&
        new Date(action.completedDate) >= startOfMonth
      ) {
        stats.completedThisMonth++;
      }

      // Calculate resolution time
      if (action.completedDate) {
        const reportedDate = new Date(action.reportedDate);
        const completedDate = new Date(action.completedDate);
        const days = Math.floor(
          (completedDate.getTime() - reportedDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        totalResolutionDays += days;
        completedCount++;
      }
    }

    // Calculate average resolution days
    if (completedCount > 0) {
      stats.averageResolutionDays = Math.round(totalResolutionDays / completedCount);
    }

    return stats;
  }

  /**
   * Search actions
   */
  async searchActions(organizationId: string, query: string): Promise<CorrectiveAction[]> {
    const allActions = await this.getByOrganization(organizationId);
    const lowerQuery = query.toLowerCase().trim();

    if (!lowerQuery) {
      return allActions;
    }

    return allActions.filter((action) => {
      return (
        action.title.toLowerCase().includes(lowerQuery) ||
        action.description.toLowerCase().includes(lowerQuery) ||
        action.category.toLowerCase().includes(lowerQuery) ||
        action.rootCause?.toLowerCase().includes(lowerQuery) ||
        action.correctiveActions?.toLowerCase().includes(lowerQuery) ||
        action.preventiveActions?.toLowerCase().includes(lowerQuery)
      );
    });
  }
}

export const correctiveActionRepository = new CorrectiveActionRepository(
  new MARSXDatabase()
);

